import {Button} from '@/components/ui/button/Button'
import {
    AlertOctagonIcon,
    ChevronDownIcon,
    LayoutAlt03Icon,
    MedicalCrossIcon,
    QueueListIcon,
    SpacingWidth01Icon,
    Trash01Icon
} from '@/components/ui/icon/Icon'
import {Menu, MenuGroup, MenuItem} from '@/components/ui/menu-atoms/MenuAtoms'
import {ProjectExtended} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'
import {routes} from '@/utilities/constants/routes'
import {MenuButton, MenuProvider} from '@ariakit/react'
import {generatePath, Link, useNavigate} from 'react-router-dom'
import {ProjectOptionsSuspendDialog} from '../project-options-suspend-dialog/ProjectOptionsSuspendDialog'
import {ProjectOptionExportSiarMenuItem} from '@/features/projects/components/project-options-dropdown/ProjectOptionExportSiarMenuItem'
import {ProjectOptionPrintSiarMenuItem} from '@/features/projects/components/project-options-dropdown/ProjectOptionPrintSiarMenuItem'
import {ProjectOptionMarkAsTransmittedMenuItem} from '@/features/projects/components/project-options-dropdown/ProjectOptionMarkAsTransmittedMenuItem'
import {useState} from 'react'
import {DeleteProjectModal} from '@/features/projects/components/delete-project-modal/DeleteProjectModal'
import {NotAllowedRenewalDialog} from '@/features/projects/components/not-allowed-renewal-dialog/NotAllowedRenewalDialog'
import {ProjectOptionLiftSuspensionDialog} from '@/features/projects/components/project-options-lift-suspension-dialog/ProjectOptionLiftSuspensionDialog'
import {usePermissions} from '@/features/auth/store/store'
import dayjs, {formatDate} from '@/dayjs'
import {ProjectSuspensionsHistoryDialog} from '../project-suspensions-history-dialog/ProjectSuspensionsHistoryDialog'

export const ProjectOptionsDropdown = ({project}: {project: ProjectExtended}) => {
    const {t} = useTranslation()
    const {id: projectId, treatmentRequestId} = project
    const permissions = usePermissions()
    const hasSuspensionsInFuture = !!project.suspensions?.some(suspension =>
        dayjs(suspension.startDate).isAfter(dayjs(), 'day')
    )
    const hasSuspensions = project.isSuspended || hasSuspensionsInFuture

    return (
        <MenuProvider>
            <MenuButton
                render={
                    <Button size="md" variant="secondary">
                        {t('commons:manage_project')}
                        <ChevronDownIcon />
                    </Button>
                }
            />
            <Menu>
                <MenuGroup>
                    {project.status == 'open' && permissions?.projects.read && permissions?.projects.write && (
                        <>
                            {hasSuspensions ? (
                                <ProjectOptionLiftSuspensionDialog project={project} />
                            ) : (
                                <ProjectOptionsSuspendDialog project={project} />
                            )}
                        </>
                    )}

                    <MenuItem
                        disabled={!project.suspensions || project.suspensions.length == 0}
                        render={<ProjectSuspensionsHistoryDialog suspensions={project.suspensions} />}
                    />

                    {project.type == 'continuous' &&
                        project.status != 'closed' &&
                        !project.renewedProjectId &&
                        permissions?.projects.read &&
                        permissions?.projects.write && (
                            <MenuItem
                                disabled={project.status != 'open'}
                                render={
                                    project.isRenewable ? (
                                        <Link to={generatePath(routes.PROJECT_RENEW.handle.path, {id: projectId})} />
                                    ) : (
                                        <NotAllowedRenewalDialog projectType={project.type} />
                                    )
                                }
                            >
                                <LayoutAlt03Icon />
                                {t('projects:renew_project_title')}
                            </MenuItem>
                        )}

                    {project.status == 'closed' && permissions?.projects.read && permissions?.projects.write && (
                        <MenuItem
                            disabled={!!project.variedProjectId}
                            render={<Link to={generatePath(routes.PROJECT_REOPENING.handle.path, {id: projectId})} />}
                        >
                            <LayoutAlt03Icon />
                            {t('projects:reopen_project')}
                        </MenuItem>
                    )}

                    {project.type == 'cyclic' && permissions?.projects.read && permissions?.projects.write && (
                        <>
                            {project.status == 'open' && (
                                <MenuItem
                                    disabled={!!(project.status != 'open' || hasSuspensions)}
                                    render={
                                        project.isRenewable ? (
                                            <Link
                                                to={generatePath(routes.PROJECT_EXTENDS_CYCLE.handle.path, {
                                                    id: projectId
                                                })}
                                            />
                                        ) : (
                                            <NotAllowedRenewalDialog projectType={project.type} />
                                        )
                                    }
                                >
                                    <SpacingWidth01Icon />
                                    {t('projects:extends_project')}
                                </MenuItem>
                            )}
                            <MenuItem
                                disabled={!!(project.status != 'open' || hasSuspensions || project.renewedAt)}
                                render={
                                    <Link to={generatePath(routes.PROJECT_TRANSFORM.handle.path, {id: projectId})} />
                                }
                            >
                                <LayoutAlt03Icon />
                                {t('projects:transform_project')}
                            </MenuItem>
                        </>
                    )}
                    {permissions?.medicalRecords?.read && (
                        <MenuItem
                            render={
                                <Link
                                    target="_blank"
                                    to={generatePath(routes.PATIENT_DETAILS_MEDICAL_RECORDS.handle.path, {
                                        id: project.patientId
                                    })}
                                />
                            }
                        >
                            <MedicalCrossIcon />
                            {t('global_search:medical_record')}
                        </MenuItem>
                    )}
                    {permissions?.agenda.read && permissions?.agenda.write && (
                        <MenuItem
                            render={
                                <Link
                                    target="_blank"
                                    to={
                                        generatePath(routes.AGENDA.path, {
                                            entity: 'patients',
                                            view: 'week',
                                            date: formatDate(new Date(), 'DD-MM-YYYY')
                                        }) + `?category=custom&subjectIds=${project.patientId}`
                                    }
                                />
                            }
                        >
                            <LayoutAlt03Icon />
                            {t('projects:patient_agenda')}
                        </MenuItem>
                    )}
                    {permissions?.requests?.read && treatmentRequestId && (
                        <MenuItem
                            render={
                                <Link
                                    to={generatePath(routes.TREATMENT_REQUEST_DETAILS_ROUTE.handle.path, {
                                        id: treatmentRequestId
                                    })}
                                />
                            }
                        >
                            <QueueListIcon />
                            {t('commons:treatment_request')}
                        </MenuItem>
                    )}
                    {permissions?.projects.read && permissions?.projects.write && (
                        <>
                            <ProjectOptionPrintSiarMenuItem project={project} />
                            <ProjectOptionExportSiarMenuItem project={project} />
                            <ProjectOptionMarkAsTransmittedMenuItem project={project} />
                        </>
                    )}
                </MenuGroup>
                {project.status == 'opening' && permissions?.projects.read && permissions?.projects.write ? (
                    <MenuGroup>
                        <MenuItem
                            disabled={!project.canDeleteProject}
                            render={<DeleteProjectMenuItem disabled={!project.canDeleteProject} project={project} />}
                        />
                    </MenuGroup>
                ) : (
                    project.status == 'open' &&
                    permissions?.projects.read &&
                    permissions?.projects.write && (
                        <MenuGroup>
                            <MenuItem
                                render={
                                    <Link
                                        to={generatePath(routes.PROJECT_EARLY_CLOSING.handle.path, {id: projectId})}
                                    />
                                }
                            >
                                <AlertOctagonIcon />

                                {t('projects:project_early_closing_title')}
                            </MenuItem>
                        </MenuGroup>
                    )
                )}
            </Menu>
        </MenuProvider>
    )
}

const DeleteProjectMenuItem = ({disabled, project}: {disabled: boolean; project: ProjectExtended}) => {
    const {t} = useTranslation()
    const [isDeleteProjectModalOpen, setIsDeleteProjectModalOpen] = useState(false)
    const navigate = useNavigate()
    return (
        <>
            <Button disabled={disabled} onClick={() => setIsDeleteProjectModalOpen(true)} variant="ghostDanger">
                <Trash01Icon />
                {t('commons:delete')}
            </Button>
            {isDeleteProjectModalOpen && (
                <DeleteProjectModal
                    project={project}
                    onClose={() => setIsDeleteProjectModalOpen(false)}
                    onSuccess={() => {
                        setIsDeleteProjectModalOpen(false)
                        navigate(routes.PROJECTS_ROUTE.path)
                    }}
                />
            )}
        </>
    )
}
